import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';

const resultsHtml = `
    <div class="container">
        <header>
            <h1>ORB Strategy Projections</h1>
            <p class="subtitle">Opening Range Breakout - Max 40 Contracts</p>
            <div class="meta">
                <span class="meta-item"><strong>Generated:</strong> December 11, 2025</span>
                <span class="meta-item"><strong>Data:</strong> 2-Year QQQ Backtest</span>
                <span class="meta-item"><strong>Period:</strong> Dec 2023 - Dec 2025</span>
                <span class="meta-item"><strong>Trades:</strong> 396 (90% fill rate)</span>
            </div>
        </header>

        <section>
            <h2>What Is This?</h2>
            <p style="margin-bottom: 1rem; color: var(--text-muted); font-size: 1.05rem; line-height: 1.8;">
                This is a <strong style="color: var(--text);">2-year backtest simulation</strong> of the <strong style="color: var(--primary);">Opening Range Breakout (ORB)</strong> strategy trading QQQ 0DTE options. The strategy enters trades when price breaks above or below the first 5-minute candle's range, using options contracts that expire the same day.
            </p>
            <p style="margin-bottom: 1rem; color: var(--text-muted); font-size: 1.05rem; line-height: 1.8;">
                The data below shows <strong style="color: var(--text);">projected returns at different starting capital levels</strong>, accounting for realistic costs like slippage, commissions, and partial fills. It answers the question: <em>"If I had started with $X and followed this strategy for 2 years, what would my account look like?"</em>
            </p>
            <p style="color: var(--text-muted); font-size: 1.05rem; line-height: 1.8;">
                <strong style="color: var(--warning);">Key insight:</strong> Position size scales with your account equity, so starting capital significantly impacts both returns and risk. Smaller accounts compound aggressively but face larger drawdowns when they hit the contract cap.
            </p>
        </section>

        <section>
            <h2>Assumptions</h2>
            <div class="assumptions-grid">
                <div class="assumption-card">
                    <div class="value">40</div>
                    <div class="label">Max Contracts</div>
                </div>
                <div class="assumption-card">
                    <div class="value">$12.00</div>
                    <div class="label">Slippage/Contract</div>
                </div>
                <div class="assumption-card">
                    <div class="value">$1.30</div>
                    <div class="label">Commission/Contract</div>
                </div>
                <div class="assumption-card">
                    <div class="value">90%</div>
                    <div class="label">Fill Rate</div>
                </div>
                <div class="assumption-card">
                    <div class="value">63.6%</div>
                    <div class="label">Win Rate</div>
                </div>
                <div class="assumption-card">
                    <div class="value">$50.73</div>
                    <div class="label">Avg Win/Contract</div>
                </div>
                <div class="assumption-card">
                    <div class="value">-$37.14</div>
                    <div class="label">Avg Loss/Contract</div>
                </div>
                <div class="assumption-card">
                    <div class="value">2.39</div>
                    <div class="label">Profit Factor</div>
                </div>
            </div>
        </section>

        <section>
            <h2>How Position Sizing Works</h2>
            <p style="margin-bottom: 1.5rem; color: var(--text-muted);">
                The simulation uses a simple compounding rule: <strong style="color: var(--primary);">contracts = min(MAX_CAP, equity / $500)</strong>
            </p>

            <div class="optimal-box" style="border-color: var(--text-muted);">
                <h3 style="color: var(--text);">Example: Starting with $1,000 (40-Contract Cap)</h3>
                <table style="margin-top: 1rem;">
                    <thead>
                        <tr>
                            <th style="text-align: left;">Phase</th>
                            <th>Equity</th>
                            <th>Calculation</th>
                            <th>Contracts Traded</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            <td style="text-align: left;">Trade 1</td>
                            <td>$1,000</td>
                            <td>$1,000 ÷ $500 = 2</td>
                            <td><strong>2 contracts</strong></td>
                        </tr>
                        <tr>
                            <td style="text-align: left;">After some wins</td>
                            <td>$2,500</td>
                            <td>$2,500 ÷ $500 = 5</td>
                            <td><strong>5 contracts</strong></td>
                        </tr>
                        <tr>
                            <td style="text-align: left;">More wins</td>
                            <td>$5,000</td>
                            <td>$5,000 ÷ $500 = 10</td>
                            <td><strong>10 contracts</strong></td>
                        </tr>
                        <tr>
                            <td style="text-align: left;">Growth phase</td>
                            <td>$10,000</td>
                            <td>$10,000 ÷ $500 = 20</td>
                            <td><strong>20 contracts</strong></td>
                        </tr>
                        <tr class="highlight-row">
                            <td style="text-align: left;">Peak (capped)</td>
                            <td>$25,000</td>
                            <td>$25,000 ÷ $500 = 50 → <span class="warning">capped at 40</span></td>
                            <td><strong>40 contracts</strong></td>
                        </tr>
                        <tr>
                            <td style="text-align: left; color: var(--danger);">Drawdown hits!</td>
                            <td style="color: var(--danger);">$15,000</td>
                            <td>$15,000 ÷ $500 = 30</td>
                            <td><strong>30 contracts</strong></td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <div style="margin-top: 1.5rem; padding: 1rem; background: var(--bg); border-radius: 0.5rem;">
                <h4 style="color: var(--primary); margin-bottom: 0.75rem;">Key Points:</h4>
                <ul style="margin-left: 1.5rem; color: var(--text-muted);">
                    <li><strong style="color: var(--text);">You never buy more than you can afford</strong> - contracts scale with equity</li>
                    <li><strong style="color: var(--text);">The "40 cap" is a ceiling, not a starting point</strong> - you start small and compound up</li>
                    <li><strong style="color: var(--text);">During drawdowns, you're still at high contract counts</strong> - this amplifies losses</li>
                    <li><strong style="color: var(--text);">Small accounts hit the cap later</strong> - but when drawdowns hit at max size, recovery is harder</li>
                </ul>
            </div>
        </section>

        <section>
            <h2>Returns by Starting Capital</h2>
            <table>
                <thead>
                    <tr>
                        <th>Start</th>
                        <th>Final (2yr)</th>
                        <th>Profit</th>
                        <th>Return</th>
                        <th>Monthly</th>
                        <th>Max DD</th>
                    </tr>
                </thead>
                <tbody>
                    <tr>
                        <td>$1,000</td>
                        <td>$6,391</td>
                        <td class="positive">$5,391</td>
                        <td class="positive">+539%</td>
                        <td>$225</td>
                        <td class="negative">74%</td>
                    </tr>
                    <tr>
                        <td>$2,500</td>
                        <td>$30,882</td>
                        <td class="positive">$28,382</td>
                        <td class="positive">+1,135%</td>
                        <td>$1,183</td>
                        <td class="warning">61%</td>
                    </tr>
                    <tr>
                        <td>$5,000</td>
                        <td>$52,119</td>
                        <td class="positive">$47,119</td>
                        <td class="positive">+942%</td>
                        <td>$1,963</td>
                        <td class="warning">40%</td>
                    </tr>
                    <tr>
                        <td>$7,500</td>
                        <td>$66,410</td>
                        <td class="positive">$58,910</td>
                        <td class="positive">+785%</td>
                        <td>$2,455</td>
                        <td class="warning">38%</td>
                    </tr>
                    <tr class="highlight-row">
                        <td><strong>$10,000</strong></td>
                        <td><strong>$74,435</strong></td>
                        <td class="positive"><strong>$64,435</strong></td>
                        <td class="positive"><strong>+644%</strong></td>
                        <td><strong>$2,685</strong></td>
                        <td><strong>34%</strong></td>
                    </tr>
                    <tr>
                        <td>$12,500</td>
                        <td>$79,749</td>
                        <td class="positive">$67,249</td>
                        <td class="positive">+538%</td>
                        <td>$2,802</td>
                        <td>29%</td>
                    </tr>
                    <tr>
                        <td>$15,000</td>
                        <td>$84,332</td>
                        <td class="positive">$69,332</td>
                        <td class="positive">+462%</td>
                        <td>$2,889</td>
                        <td>27%</td>
                    </tr>
                    <tr>
                        <td>$17,500</td>
                        <td>$89,089</td>
                        <td class="positive">$71,589</td>
                        <td class="positive">+409%</td>
                        <td>$2,983</td>
                        <td>27%</td>
                    </tr>
                    <tr class="highlight-row">
                        <td><strong>$20,000</strong></td>
                        <td><strong>$92,042</strong></td>
                        <td class="positive"><strong>$72,042</strong></td>
                        <td class="positive"><strong>+360%</strong></td>
                        <td><strong>$3,002</strong></td>
                        <td><strong>26%</strong></td>
                    </tr>
                </tbody>
            </table>
        </section>

        <section>
            <h2>Monthly Income Breakdown</h2>
            <table>
                <thead>
                    <tr>
                        <th>Start</th>
                        <th>Monthly</th>
                        <th>Weekly</th>
                        <th>Per Trade</th>
                        <th>Annual</th>
                    </tr>
                </thead>
                <tbody>
                    <tr>
                        <td>$1,000</td>
                        <td>$225</td>
                        <td>$56</td>
                        <td>$13.61</td>
                        <td>$2,695</td>
                    </tr>
                    <tr>
                        <td>$2,500</td>
                        <td>$1,183</td>
                        <td>$296</td>
                        <td>$71.67</td>
                        <td>$14,191</td>
                    </tr>
                    <tr>
                        <td>$5,000</td>
                        <td>$1,963</td>
                        <td>$491</td>
                        <td>$118.99</td>
                        <td>$23,560</td>
                    </tr>
                    <tr>
                        <td>$7,500</td>
                        <td>$2,455</td>
                        <td>$614</td>
                        <td>$148.76</td>
                        <td>$29,455</td>
                    </tr>
                    <tr class="highlight-row">
                        <td><strong>$10,000</strong></td>
                        <td><strong>$2,685</strong></td>
                        <td><strong>$671</strong></td>
                        <td><strong>$162.71</strong></td>
                        <td><strong>$32,217</strong></td>
                    </tr>
                    <tr>
                        <td>$12,500</td>
                        <td>$2,802</td>
                        <td>$701</td>
                        <td>$169.82</td>
                        <td>$33,625</td>
                    </tr>
                    <tr>
                        <td>$15,000</td>
                        <td>$2,889</td>
                        <td>$722</td>
                        <td>$175.08</td>
                        <td>$34,666</td>
                    </tr>
                    <tr>
                        <td>$17,500</td>
                        <td>$2,983</td>
                        <td>$746</td>
                        <td>$180.78</td>
                        <td>$35,795</td>
                    </tr>
                    <tr class="highlight-row">
                        <td><strong>$20,000</strong></td>
                        <td><strong>$3,002</strong></td>
                        <td><strong>$750</strong></td>
                        <td><strong>$181.92</strong></td>
                        <td><strong>$36,021</strong></td>
                    </tr>
                </tbody>
            </table>
        </section>

        <section>
            <h2>20 vs 40 Contract Comparison</h2>
            <table>
                <thead>
                    <tr>
                        <th>Start</th>
                        <th>20-Cap Profit</th>
                        <th>40-Cap Profit</th>
                        <th>Extra Profit</th>
                        <th>Extra DD</th>
                    </tr>
                </thead>
                <tbody>
                    <tr>
                        <td>$1,000</td>
                        <td>$7,159</td>
                        <td>$5,391</td>
                        <td class="negative">-$1,768</td>
                        <td class="negative">+3.5%</td>
                    </tr>
                    <tr>
                        <td>$2,500</td>
                        <td>$23,084</td>
                        <td>$28,382</td>
                        <td class="positive">+$5,298</td>
                        <td class="warning">+20.0%</td>
                    </tr>
                    <tr>
                        <td>$5,000</td>
                        <td>$31,815</td>
                        <td>$47,119</td>
                        <td class="positive">+$15,304</td>
                        <td>+6.3%</td>
                    </tr>
                    <tr>
                        <td>$7,500</td>
                        <td>$34,630</td>
                        <td>$58,910</td>
                        <td class="positive">+$24,280</td>
                        <td>+11.5%</td>
                    </tr>
                    <tr class="highlight-row">
                        <td><strong>$10,000</strong></td>
                        <td><strong>$35,965</strong></td>
                        <td><strong>$64,435</strong></td>
                        <td class="positive"><strong>+$28,470</strong></td>
                        <td><strong>+7.1%</strong></td>
                    </tr>
                    <tr>
                        <td>$15,000</td>
                        <td>$36,350</td>
                        <td>$69,332</td>
                        <td class="positive">+$32,981</td>
                        <td>+5.2%</td>
                    </tr>
                    <tr class="highlight-row">
                        <td><strong>$20,000</strong></td>
                        <td><strong>$36,350</strong></td>
                        <td><strong>$72,042</strong></td>
                        <td class="positive"><strong>+$35,691</strong></td>
                        <td><strong>+6.4%</strong></td>
                    </tr>
                </tbody>
            </table>
        </section>

        <section>
            <h2>Sweet Spot Analysis</h2>
            <div class="sweet-spot-grid">
                <div class="sweet-spot-card">
                    <h4>Best ROI %</h4>
                    <div class="detail"><strong>Starting Capital:</strong> $2,500-$5,000</div>
                    <div class="detail"><strong>Return:</strong> 940-1,135%</div>
                    <div class="detail"><strong>Risk:</strong> Higher drawdowns (40-61%)</div>
                </div>
                <div class="sweet-spot-card">
                    <h4>Best Risk/Reward</h4>
                    <div class="detail"><strong>Starting Capital:</strong> $10,000-$15,000</div>
                    <div class="detail"><strong>Return:</strong> 462-644%</div>
                    <div class="detail"><strong>Drawdown:</strong> 27-34%</div>
                </div>
                <div class="sweet-spot-card">
                    <h4>Best Monthly Income</h4>
                    <div class="detail"><strong>Starting Capital:</strong> $20,000+</div>
                    <div class="detail"><strong>Monthly:</strong> ~$3,000 (caps out)</div>
                    <div class="detail"><strong>Annual:</strong> ~$36,000</div>
                </div>
            </div>

            <div class="optimal-box">
                <h3>Optimal Range: $10,000 - $20,000</h3>
                <div class="optimal-grid">
                    <div class="optimal-item">
                        <div class="value">$64K-$72K</div>
                        <div class="label">2-Year Profit</div>
                    </div>
                    <div class="optimal-item">
                        <div class="value">$2,700-$3,000</div>
                        <div class="label">Monthly Income</div>
                    </div>
                    <div class="optimal-item">
                        <div class="value">26-34%</div>
                        <div class="label">Max Drawdown</div>
                    </div>
                    <div class="optimal-item">
                        <div class="value">~$2,000</div>
                        <div class="label">Per-Trade Swing</div>
                    </div>
                </div>
            </div>
        </section>

        <section>
            <h2>Risk Warnings</h2>
            <div class="warning-box">
                <h4>⚠️ Important Considerations</h4>
                <ul>
                    <li><strong>Small accounts ($1K) can perform WORSE with a higher cap</strong> - once you compound UP to 40 contracts during winning streaks, the inevitable drawdowns at that size erase more gains than a lower cap would allow</li>
                    <li><strong>Per-trade swings of $2,000+</strong> - a single bad trade can cost $1,500-$2,000</li>
                    <li><strong>74% max drawdown possible</strong> with small starting capital</li>
                    <li><strong>Liquidity concerns</strong> - 40 contracts may face partial fills on illiquid strikes</li>
                    <li><strong>0 DTE options</strong> - extreme gamma risk, prices can move 50-100% in minutes</li>
                </ul>
            </div>
        </section>

        <section>
            <h2>Backtest vs Reality</h2>
            <div class="comparison-section">
                <div class="comparison-card">
                    <h4>Ideal Backtest</h4>
                    <table>
                        <tr>
                            <td>2-Year Return</td>
                            <td class="positive">+$268,795</td>
                        </tr>
                        <tr>
                            <td>Percentage</td>
                            <td class="positive">+2,688%</td>
                        </tr>
                    </table>
                </div>
                <div class="comparison-card">
                    <h4>With Slippage Only</h4>
                    <table>
                        <tr>
                            <td>2-Year Return</td>
                            <td class="positive">+$44,106</td>
                        </tr>
                        <tr>
                            <td>Percentage</td>
                            <td class="positive">+441%</td>
                        </tr>
                    </table>
                </div>
                <div class="comparison-card">
                    <h4>Realistic (All Costs)</h4>
                    <table>
                        <tr>
                            <td>2-Year Return</td>
                            <td class="positive">+$64,435</td>
                        </tr>
                        <tr>
                            <td>Percentage</td>
                            <td class="positive">+644%</td>
                        </tr>
                    </table>
                </div>
            </div>
            <p style="color: var(--text-muted); marginTop: '1rem'; textAlign: 'center';">
                Reality is ~24% of ideal backtest returns due to bid-ask spread, commissions, and missed fills.
            </p>
        </section>

        <footer>
            <p>Generated by Marcus Baker - December 11, 2025</p>
            <p style="marginTop: '0.5rem';">
                Data: QQQ 5-min candles (Polygon.io) | Options: 1.3M cached quotes | Strategy: Opening Range Breakout
            </p>
        </footer>
    </div>
`;

const styles = `
    :root {
        --primary: #2563eb;
        --primary-dark: #1d4ed8;
        --success: #16a34a;
        --warning: #ea580c;
        --danger: #dc2626;
        --bg: #0f172a;
        --bg-card: #1e293b;
        --bg-table: #334155;
        --text: #f1f5f9;
        --text-muted: #94a3b8;
        --border: #475569;
    }

    .results-page * {
        margin: 0;
        padding: 0;
        box-sizing: border-box;
    }

    .results-page {
        font-family: 'Segoe UI', system-ui, -apple-system, sans-serif;
        background: var(--bg);
        color: var(--text);
        line-height: 1.6;
        padding: 2rem;
        min-height: 100vh;
    }

    .results-page .container {
        max-width: 1200px;
        margin: 0 auto;
    }

    .results-page header {
        text-align: center;
        margin-bottom: 3rem;
        padding-bottom: 2rem;
        border-bottom: 2px solid var(--border);
    }

    .results-page h1 {
        font-size: 2.5rem;
        color: var(--primary);
        margin-bottom: 0.5rem;
    }

    .results-page .subtitle {
        color: var(--text-muted);
        font-size: 1.1rem;
    }

    .results-page .meta {
        display: flex;
        justify-content: center;
        gap: 2rem;
        margin-top: 1rem;
        flex-wrap: wrap;
    }

    .results-page .meta-item {
        background: var(--bg-card);
        padding: 0.5rem 1rem;
        border-radius: 0.5rem;
        font-size: 0.9rem;
    }

    .results-page .meta-item strong {
        color: var(--primary);
    }

    .results-page section {
        background: var(--bg-card);
        border-radius: 1rem;
        padding: 1.5rem;
        margin-bottom: 2rem;
        border: 1px solid var(--border);
    }

    .results-page h2 {
        color: var(--primary);
        font-size: 1.5rem;
        margin-bottom: 1rem;
        padding-bottom: 0.5rem;
        border-bottom: 1px solid var(--border);
    }

    .results-page h3 {
        color: var(--text);
        font-size: 1.2rem;
        margin: 1.5rem 0 1rem 0;
    }

    .results-page table {
        width: 100%;
        border-collapse: collapse;
        margin: 1rem 0;
        font-size: 0.95rem;
    }

    .results-page th {
        background: var(--bg-table);
        color: var(--primary);
        padding: 0.75rem;
        text-align: right;
        font-weight: 600;
        border-bottom: 2px solid var(--border);
    }

    .results-page th:first-child {
        text-align: left;
    }

    .results-page td {
        padding: 0.75rem;
        text-align: right;
        border-bottom: 1px solid var(--border);
    }

    .results-page td:first-child {
        text-align: left;
        font-weight: 500;
    }

    .results-page tr:hover {
        background: rgba(37, 99, 235, 0.1);
    }

    .results-page .highlight-row {
        background: rgba(37, 99, 235, 0.15) !important;
    }

    .results-page .positive {
        color: var(--success);
    }

    .results-page .negative {
        color: var(--danger);
    }

    .results-page .warning {
        color: var(--warning);
    }

    .results-page .assumptions-grid {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
        gap: 1rem;
    }

    .results-page .assumption-card {
        background: var(--bg);
        padding: 1rem;
        border-radius: 0.5rem;
        text-align: center;
    }

    .results-page .assumption-card .value {
        font-size: 1.5rem;
        font-weight: 700;
        color: var(--primary);
    }

    .results-page .assumption-card .label {
        color: var(--text-muted);
        font-size: 0.85rem;
        margin-top: 0.25rem;
    }

    .results-page .optimal-box {
        background: linear-gradient(135deg, rgba(37, 99, 235, 0.2), rgba(37, 99, 235, 0.05));
        border: 2px solid var(--primary);
        border-radius: 1rem;
        padding: 1.5rem;
        margin: 1.5rem 0;
    }

    .results-page .optimal-box h3 {
        color: var(--primary);
        margin-top: 0;
    }

    .results-page .optimal-grid {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
        gap: 1rem;
        margin-top: 1rem;
    }

    .results-page .optimal-item {
        text-align: center;
    }

    .results-page .optimal-item .value {
        font-size: 1.3rem;
        font-weight: 700;
        color: var(--success);
    }

    .results-page .optimal-item .label {
        color: var(--text-muted);
        font-size: 0.85rem;
    }

    .results-page .warning-box {
        background: rgba(220, 38, 38, 0.1);
        border: 1px solid var(--danger);
        border-radius: 0.5rem;
        padding: 1rem;
        margin: 1rem 0;
    }

    .results-page .warning-box h4 {
        color: var(--danger);
        margin-bottom: 0.5rem;
    }

    .results-page .warning-box ul {
        margin-left: 1.5rem;
        color: var(--text-muted);
    }

    .results-page .warning-box li {
        margin: 0.5rem 0;
    }

    .results-page .comparison-section {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
        gap: 1.5rem;
    }

    .results-page .comparison-card {
        background: var(--bg);
        border-radius: 0.75rem;
        padding: 1.25rem;
    }

    .results-page .comparison-card h4 {
        color: var(--text);
        margin-bottom: 0.75rem;
    }

    .results-page .sweet-spot-grid {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
        gap: 1rem;
    }

    .results-page .sweet-spot-card {
        background: var(--bg);
        border-radius: 0.75rem;
        padding: 1.25rem;
        border-left: 4px solid var(--primary);
    }

    .results-page .sweet-spot-card h4 {
        color: var(--primary);
        margin-bottom: 0.5rem;
    }

    .results-page .sweet-spot-card .detail {
        color: var(--text-muted);
        font-size: 0.9rem;
        margin: 0.25rem 0;
    }

    .results-page .sweet-spot-card .detail strong {
        color: var(--text);
    }

    .results-page footer {
        text-align: center;
        color: var(--text-muted);
        margin-top: 3rem;
        padding-top: 2rem;
        border-top: 1px solid var(--border);
        font-size: 0.9rem;
    }

    .access-denied {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        min-height: 100vh;
        background: #0f172a;
        color: #f1f5f9;
        font-family: 'Segoe UI', system-ui, -apple-system, sans-serif;
    }

    .access-denied h1 {
        color: #dc2626;
        font-size: 2rem;
        margin-bottom: 1rem;
    }

    .access-denied p {
        color: #94a3b8;
    }

    .loading {
        display: flex;
        align-items: center;
        justify-content: center;
        min-height: 100vh;
        background: #0f172a;
        color: #f1f5f9;
        font-family: 'Segoe UI', system-ui, -apple-system, sans-serif;
    }

    @media (max-width: 768px) {
        .results-page {
            padding: 1rem;
        }

        .results-page h1 {
            font-size: 1.75rem;
        }

        .results-page table {
            font-size: 0.8rem;
        }

        .results-page th, .results-page td {
            padding: 0.5rem;
        }
    }
`;

function BacktestResults() {
  const [searchParams] = useSearchParams();
  const [isValid, setIsValid] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const validateKey = async () => {
      const key = searchParams.get('key');

      if (!key) {
        setIsValid(false);
        setIsLoading(false);
        return;
      }

      try {
        const response = await fetch(`/api/validate-key?key=${encodeURIComponent(key)}`);
        const data = await response.json();
        setIsValid(data.valid);
      } catch (error) {
        setIsValid(false);
      }

      setIsLoading(false);
    };

    validateKey();
  }, [searchParams]);

  if (isLoading) {
    return (
      <>
        <style>{styles}</style>
        <div className="loading">
          <p>Validating access...</p>
        </div>
      </>
    );
  }

  if (!isValid) {
    return (
      <>
        <style>{styles}</style>
        <div className="access-denied">
          <h1>Access Denied</h1>
          <p>Invalid or missing access key.</p>
        </div>
      </>
    );
  }

  return (
    <>
      <style>{styles}</style>
      <div className="results-page" dangerouslySetInnerHTML={{ __html: resultsHtml }} />
    </>
  );
}

export default BacktestResults;
